import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { getBooks } from '../features/books/bookSlice';
import BookCard from '../components/BookCard';
import BookCardSkeleton from '../components/BookCardSkeleton';

const Books = () => {
  const { category } = useParams();
  const dispatch = useDispatch();
  const { books, isLoading } = useSelector((state) => state.books);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const booksPerPage = 8;

  useEffect(() => {
    dispatch(getBooks());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [category]);

  // Filter books by search and category
  const filteredBooks = books.filter(book => {
    const matchesSearch = book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          book.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          book.isbn.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = category ? book.category.toLowerCase() === category.toLowerCase().replace(/-/g, ' ') : true;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Get unique categories for filter
  const uniqueCategories = [...new Set(books.map(book => book.category))];

  const getPageTitle = () => {
    if (category) {
      return `Buku ${category.replace(/-/g, ' ').charAt(0).toUpperCase() + category.replace(/-/g, ' ').slice(1)}`;
    }
    return 'Koleksi Buku';
  };

  return (
    <div className="py-12">
      <section className="container mx-auto px-4">
        <div className="mb-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{getPageTitle()}</h1>
              <p className="text-gray-500">Jelajahi seluruh koleksi buku terbitan Penerbit Polbis Pess</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Cari buku..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              />
              <select
                value={category || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    window.location.href = `/books/category/${e.target.value.toLowerCase().replace(/\s+/g, '-')}`;
                  } else {
                    window.location.href = '/books';
                  }
                }}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              >
                <option value="">Semua Kategori</option>
                {uniqueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array(8).fill(0).map((_, index) => <BookCardSkeleton key={index} />)}
          </div>
        ) : filteredBooks.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentBooks.map(book => (
                <BookCard key={book._id} book={book} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Sebelumnya
                </button>
                {pageNumbers.map(number => (
                  <button
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      currentPage === number
                        ? 'bg-dark-blue-700 text-white'
                        : 'border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {number}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Selanjutnya
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Buku tidak ditemukan</h3>
            <p className="text-gray-500 mb-6">Tidak ada buku yang sesuai dengan kriteria pencarian</p>
            <Link to="/books" className="inline-flex items-center gap-2 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Kembali ke koleksi
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default Books;
